import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, User, Clock, ArrowLeft, ChevronRight, Copy, Scale, Calendar, TrendingUp, Utensils } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Saved reply templates
const SAVED_REPLIES = {
  "Meal plan / recipes": "If you want variety, use the Swap button and pick one of the other recipe options for that meal. Nothing will ever disappear or change on its own. If you tell me which meal you're stuck on (breakfast/lunch/dinner/snack), I'll point you to a few good swaps.",
  "Logging or tracking": "No stress — if you miss a log, just continue as normal. The goal is trends, not perfection. Log your meals when you can, and try to keep weekly check-ins consistent so I can adjust your plan accurately.",
  "Eating out / free meals": "Yes — you can eat out. Keep it to 1 free meal per week and replace a meal from your plan. If it's a big meal (pizza, burger + extras), replace 2 meals. Try to skip unnecessary extras, choose diet drinks, and get back on track the next day like normal.",
  "Weight changes": "Small jumps up or down are normal — water, salt, sleep, and stress can move the scale. Keep weighing in when you can and focus on the weekly trend. If you've been consistent, I'll make adjustments when the trend shows we need it.",
  "Billing / account": "For billing or account issues, tell me what happened (what you tried + what you expected). If there's an error message, paste it here. I'll help you sort it as fast as possible.",
  "Something else": "Got it — tell me what you expected to happen and what actually happened. If it's a bug, include the page you were on and the last thing you clicked."
};

// Format bucket for display
const formatBucket = (bucket) => {
  if (!bucket) return 'Not assigned';
  const bucketMap = {
    'on_track_high': '🟢 On Track (High)',
    'on_track_medium': '🟢 On Track (Medium)',
    'on_track_low': '🔴 On Track (Low)',
    'plateau_high': '🟡 Plateau (High)',
    'plateau_medium': '🟡 Plateau (Medium)',
    'plateau_low': '🔴 Plateau (Low)',
    'slow_high': '🟡 Slow Progress (High)',
    'slow_medium': '🟡 Slow Progress (Medium)',
    'slow_low': '🔴 Slow Progress (Low)',
    'too_fast_high': '🟠 Too Fast (High)',
    'too_fast_medium': '🟠 Too Fast (Medium)',
    'too_fast_low': '🔴 Too Fast (Low)',
    'going_up_high': '🟠 Going Up (High)',
    'going_up_medium': '🟠 Going Up (Medium)',
    'going_up_low': '🔴 Going Up (Low)',
    'week_1_baseline': '📋 Week 1 (Baseline)',
    'week_2_monitoring': '📋 Week 2 (Monitoring)'
  };
  return bucketMap[bucket] || bucket;
};

export default function CoachMessages({ user, onLogout }) {
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientContext, setClientContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showSavedReplies, setShowSavedReplies] = useState(true);

  useEffect(() => {
    fetchMessages();
    fetchClients();
  }, []);

  useEffect(() => {
    // Check if a client was pre-selected from navigation (e.g., from ClientProfile)
    if (location.state?.selectedClientId && clients.length > 0) {
      setSelectedClient(location.state.selectedClientId);
    }
  }, [location.state, clients]);

  useEffect(() => {
    if (selectedClient) {
      fetchClientContext(selectedClient);
    }
  }, [selectedClient]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`);
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/coach/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const fetchClientContext = async (clientId) => {
    try {
      const response = await axios.get(`${API}/messages/client-context/${clientId}`);
      setClientContext(response.data);
    } catch (error) {
      console.error('Failed to load client context');
      setClientContext(null);
    }
  };

  // Group messages by client
  const getMessagesByClient = () => {
    const grouped = {};
    
    messages.forEach(msg => {
      const clientId = msg.from_user_id === user.id ? msg.to_user_id : msg.from_user_id;
      
      if (!grouped[clientId]) {
        grouped[clientId] = {
          clientId,
          messages: [],
          lastMessage: null,
          lastCategory: null
        };
      }
      
      grouped[clientId].messages.push(msg);
      
      if (!grouped[clientId].lastMessage || 
          new Date(msg.created_at) > new Date(grouped[clientId].lastMessage.created_at)) {
        grouped[clientId].lastMessage = msg;
        if (msg.from_user_id !== user.id) {
          grouped[clientId].lastCategory = msg.category;
        }
      }
    });
    
    return Object.values(grouped).sort((a, b) => 
      new Date(b.lastMessage?.created_at) - new Date(a.lastMessage?.created_at)
    );
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.first_name} ${client.last_name}` : 'Unknown Client';
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim() || !selectedClient) return;

    setSending(true);

    try {
      await axios.post(`${API}/messages`, null, {
        params: {
          to_user_id: selectedClient,
          category: 'Coach Reply',
          body: replyText
        }
      });
      
      toast.success('Reply sent!');
      setReplyText('');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const insertSavedReply = (template) => {
    setReplyText(template);
    toast.success('Template inserted. Edit if needed, then send.');
  };

  const clientConversations = getMessagesByClient();
  const selectedConversation = selectedClient 
    ? clientConversations.find(c => c.clientId === selectedClient)
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <p>Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          <p className="text-gray-600 text-sm">View and respond to client messages</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Conversation List */}
          <div className="lg:col-span-3">
            <Card className="h-[calc(100vh-180px)] overflow-hidden">
              <CardHeader className="py-3 px-4 border-b bg-gray-50">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-blue-600" />
                  Conversations ({clientConversations.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-y-auto h-full">
                {clientConversations.length === 0 ? (
                  <p className="text-gray-500 text-center py-8 text-sm">No messages yet</p>
                ) : (
                  <div className="divide-y">
                    {clientConversations.map((convo) => (
                      <button
                        key={convo.clientId}
                        onClick={() => setSelectedClient(convo.clientId)}
                        className={`w-full text-left p-3 hover:bg-gray-50 transition ${
                          selectedClient === convo.clientId ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm text-gray-900 truncate flex-1">
                            {getClientName(convo.clientId)}
                          </span>
                          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </div>
                        {convo.lastCategory && (
                          <Badge variant="outline" className="text-xs mb-1 bg-blue-50 text-blue-700 border-blue-200">
                            {convo.lastCategory}
                          </Badge>
                        )}
                        <p className="text-xs text-gray-500 truncate">
                          {convo.lastMessage?.body?.substring(0, 50)}...
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {convo.lastMessage && new Date(convo.lastMessage.created_at).toLocaleDateString()}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Message Thread */}
          <div className="lg:col-span-5">
            <Card className="h-[calc(100vh-180px)] flex flex-col">
              <CardHeader className="py-3 px-4 border-b bg-gray-50 flex-shrink-0">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  {selectedClient ? (
                    <>
                      <User className="h-4 w-4 text-blue-600" />
                      {getClientName(selectedClient)}
                    </>
                  ) : (
                    <>
                      <MessageSquare className="h-4 w-4 text-gray-400" />
                      Select a conversation
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0 flex flex-col">
                {!selectedClient ? (
                  <div className="flex-1 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <MessageSquare className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">Select a conversation to view messages</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                      {selectedConversation?.messages
                        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                        .map((message) => {
                          const isFromMe = message.from_user_id === user.id;
                          
                          return (
                            <div key={message.id} className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[85%]`}>
                                {!isFromMe && message.category && (
                                  <Badge variant="outline" className="text-xs mb-1 bg-gray-100 text-gray-600">
                                    {message.category}
                                  </Badge>
                                )}
                                <div className={`p-3 rounded-lg text-sm ${
                                  isFromMe 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-gray-100 text-gray-900'
                                }`}>
                                  {message.body}
                                </div>
                                <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${isFromMe ? 'justify-end' : ''}`}>
                                  <Clock className="h-3 w-3" />
                                  {new Date(message.created_at).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>

                    {/* Reply Form */}
                    <div className="border-t p-3 bg-gray-50 flex-shrink-0">
                      <form onSubmit={handleSendReply} className="flex gap-2">
                        <Textarea
                          placeholder="Type your reply..."
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          rows={2}
                          className="flex-1 text-sm resize-none"
                        />
                        <Button
                          type="submit"
                          disabled={sending || !replyText.trim()}
                          className="bg-blue-600 hover:bg-blue-700 self-end"
                          size="sm"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </form>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel: Context + Saved Replies */}
          <div className="lg:col-span-4 space-y-4">
            {/* Client Context Panel */}
            {selectedClient && clientContext && (
              <Card>
                <CardHeader className="py-3 px-4 border-b bg-gray-50">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4 text-blue-600" />
                    Client Context
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Name</span>
                    <span className="font-medium">{clientContext.client_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Client ID</span>
                    <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{clientContext.client_id?.slice(0, 8)}...</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Program Week
                    </span>
                    <Badge className="bg-blue-100 text-blue-700">Week {clientContext.program_week}</Badge>
                  </div>
                  <div className="flex items-col justify-between border-t pt-2">
                    <span className="text-gray-500 flex items-center gap-1 text-sm font-medium mb-1">
                      <Utensils className="h-3 w-3" /> Calorie History
                    </span>
                    <div className="text-xs space-y-0.5">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Starting:</span>
                        <span className="font-medium">{clientContext.initial_calories || clientContext.target_calories || 'N/A'} kcal</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Current:</span>
                        <span className="font-semibold text-blue-600">{clientContext.target_calories || 'N/A'} kcal</span>
                      </div>
                      {clientContext.initial_calories && clientContext.target_calories && clientContext.initial_calories !== clientContext.target_calories && (
                        <div className="flex justify-between pt-1 border-t">
                          <span className="text-gray-400">Change:</span>
                          <span className={`font-medium ${clientContext.target_calories > clientContext.initial_calories ? 'text-green-600' : 'text-red-600'}`}>
                            {clientContext.target_calories > clientContext.initial_calories ? '+' : ''}
                            {clientContext.target_calories - clientContext.initial_calories} kcal
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Last Check-in</span>
                    <span>{clientContext.last_checkin_date || 'None'}</span>
                  </div>
                  {clientContext.last_adjustment_summary && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Last Adjustment</span>
                      <span className="text-xs">{clientContext.last_adjustment_summary}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Saved Replies Panel */}
            {selectedClient && (
              <Card>
                <CardHeader className="py-3 px-4 border-b bg-gray-50">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Copy className="h-4 w-4 text-green-600" />
                      Saved replies
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowSavedReplies(!showSavedReplies)}
                      className="text-xs h-6"
                    >
                      {showSavedReplies ? 'Hide' : 'Show'}
                    </Button>
                  </CardTitle>
                </CardHeader>
                {showSavedReplies && (
                  <CardContent className="p-2 max-h-[300px] overflow-y-auto">
                    <div className="space-y-2">
                      {Object.entries(SAVED_REPLIES).map(([category, template]) => (
                        <button
                          key={category}
                          onClick={() => insertSavedReply(template)}
                          className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Badge variant="outline" className="text-xs bg-gray-50">
                              {category}
                            </Badge>
                            <span className="text-xs text-blue-600">Insert →</span>
                          </div>
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {template.substring(0, 100)}...
                          </p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
